import { useState, useEffect } from 'react';
import { apiService } from '../../../services/api';
import smmApi from '../../../services/smmApi';
import { DataFreshnessBanner, PREPARING_POLL, PreparingNotice, useBackgroundRefresh } from './DataFreshness';

const platformIcons: Record<string, string> = {
  LinkedIn: 'ri-linkedin-box-line',
  Instagram: 'ri-instagram-line',
  Twitter: 'ri-twitter-x-line',
  YouTube: 'ri-youtube-line',
  Facebook: 'ri-facebook-circle-line',
};

const labelBadgeClass = (label: string) => {
  switch (label) {
    case 'Improving': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'Stable': return 'bg-sky-50 text-sky-700 border-sky-200';
    case 'Declining': return 'bg-amber-50 text-amber-700 border-amber-200';
    default: return 'bg-gray-50 text-gray-500 border-gray-200';
  }
};

const bucketMeta: Record<string, { bg: string; border: string; iconText: string }> = {
  'bg-emerald-50 text-emerald-700 border-emerald-200': { bg: 'bg-emerald-50', border: 'border-emerald-200', iconText: 'text-emerald-700' },
  'bg-red-50 text-red-700 border-red-200': { bg: 'bg-red-50', border: 'border-red-200', iconText: 'text-red-700' },
  'bg-amber-50 text-amber-700 border-amber-200': { bg: 'bg-amber-50', border: 'border-amber-200', iconText: 'text-amber-700' },
  'bg-violet-50 text-violet-700 border-violet-200': { bg: 'bg-violet-50', border: 'border-violet-200', iconText: 'text-violet-700' },
  'bg-gray-50 text-gray-700 border-gray-200': { bg: 'bg-gray-50', border: 'border-gray-200', iconText: 'text-gray-700' },
  'bg-sky-50 text-sky-700 border-sky-200': { bg: 'bg-sky-50', border: 'border-sky-200', iconText: 'text-sky-700' },
};

// No score until a post is live (shown "—", not 0).
const _DEFAULT_CPS = {
  score: null, engagement: null, sentiment: null, reach: null, historical: null, format: null, platform: null,
};

function currentMonth() {
  return new Date().toISOString().slice(0, 7);
}

// "2026-10" → "October 2026" (built from parts: "2026-10-01" parses as UTC and can land in September).
function monthLabel(yyyymm: string) {
  const [y, m] = yyyymm.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

export default function PerformanceIntelligenceEngine() {
  const [performancePosts, setPerformancePosts]               = useState<any[]>([]);
  const [cpsBreakdown, setCpsBreakdown]                       = useState<any>(_DEFAULT_CPS);
  const [audienceSentimentBuckets, setAudienceSentimentBuckets] = useState<any[]>([]);
  const [themeData, setThemeData]                             = useState<any[]>([]);
  const [platformTracking, setPlatformTracking]               = useState<any[]>([]);
  const [selectedPost, setSelectedPost]                       = useState<any>(null);
  const [isLoading, setIsLoading]                             = useState(true);
  // Load failure (shown with Retry) vs. no calendar for this month (404).
  const [loadError, setLoadError]                             = useState<string | null>(null);
  const [noPlan, setNoPlan]                                   = useState(false);
  // Month of the calendar the backend used — the latest one when this month has none.
  const [calendarMonth, setCalendarMonth]                     = useState<string | null>(null);
  const [reloadKey, setReloadKey]                             = useState(0);
  // When the shown numbers were computed, and whether newer ones are on their way.
  const [freshness, setFreshness]                             = useState<{ updatedAt: string | null; refreshing: boolean }>({
    updatedAt: null,
    refreshing: false,
  });
  // Nothing stored yet: the first result is being prepared.
  const [preparing, setPreparing]                             = useState(false);

  const userId = localStorage.getItem('user_id') || '';
  const month  = currentMonth();

  // Puts an answer on the page (the selected post stays selected when it is still there).
  const applyAnswer = (data: any) => {
    if (data?.preparing) {
      setPreparing(true);
      return;
    }
    setPreparing(false);
    const posts = data?.performancePosts || [];
    setPerformancePosts(posts);
    setCpsBreakdown(data?.cpsBreakdown || _DEFAULT_CPS);
    setAudienceSentimentBuckets(data?.audienceSentimentBuckets || []);
    setThemeData(data?.themeData || []);
    setPlatformTracking(data?.platformTracking || []);
    setCalendarMonth(data?.calendarMonth || null);
    if (posts.length > 0) {
      // Keep the user's pick; otherwise open on the latest published post — the
      // first planned post is usually not live yet, so every metric read "—".
      const latestLive = [...posts].reverse().find((p: any) => p.matched);
      setSelectedPost((prev: any) => posts.find((p: any) => p.id === prev?.id) ?? latestLive ?? posts[0]);
    }
    setFreshness({ updatedAt: data?.updated_at ?? null, refreshing: Boolean(data?.refreshing) });
  };

  useEffect(() => {
    if (!userId) { setIsLoading(false); return; }
    setIsLoading(true);
    setLoadError(null);
    setNoPlan(false);
    setPreparing(false);
    smmApi.getSmmPerformance(userId, month)
      .then((data: any) => applyAnswer(data))
      .catch((err: any) => {
        console.error('PerformanceIntelligenceEngine: fetch error', err);
        if (err?.response?.status === 404) {
          setNoPlan(true);
        } else {
          setLoadError(err?.message || "Couldn't load performance data.");
        }
      })
      .finally(() => setIsLoading(false));
  }, [userId, month, reloadKey]);

  // Re-ask quietly (no skeleton) and swap the answer in. refresh=true is the
  // Refresh button: the server starts computing newer numbers in the background.
  const refetchQuietly = async (refresh = false) => {
    if (!userId) return;
    const data = await apiService.get<any>(
      `/smm/governance/performance/${userId}/${month}${refresh ? '?refresh=true' : ''}`,
    );
    applyAnswer(data);
  };
  // The stored numbers are shown at once; while the server computes newer ones
  // (or prepares the first result) keep asking and swap them in.
  const freshnessState = useBackgroundRefresh(freshness.refreshing, () => refetchQuietly());
  const preparingState = useBackgroundRefresh(preparing, () => refetchQuietly(), PREPARING_POLL);

  // "—" for a missing value: the backend sends null for what it doesn't know (impressions, CTR,
  // watch time, shares/reach where the platform doesn't return them), and saves aren't returned
  // by any platform API — never an invented or zero value.
  const fmtCount = (v: any) => (typeof v === 'number' ? v.toLocaleString() : '—');
  const fmtPct = (v: any) => (typeof v === 'number' ? `${v}%` : '—');
  // A planned post with no matching live post: not published yet (or its caption changed too much
  // to match), so it has no metrics — say so instead of showing 0 likes / 0 comments.
  const notLive = (p: any) => p?.matched === false;

  const metrics = selectedPost ? [
    { label: 'Likes',       value: fmtCount(selectedPost.likes),                             icon: 'ri-heart-3-line',        color: 'text-rose-500'   },
    { label: 'Comments',    value: fmtCount(selectedPost.comments),                          icon: 'ri-chat-3-line',         color: 'text-violet-500' },
    { label: 'Shares',      value: fmtCount(selectedPost.shares),                            icon: 'ri-share-forward-line',  color: 'text-sky-500'    },
    { label: 'Saves',       value: '—',                                                      icon: 'ri-bookmark-line',       color: 'text-amber-500'  },
    { label: 'Impressions', value: fmtCount(selectedPost.impressions),                       icon: 'ri-eye-line',            color: 'text-gray-500'   },
    { label: 'Reach',       value: fmtCount(selectedPost.reach),                             icon: 'ri-user-line',           color: 'text-emerald-500'},
    { label: 'Watch Time',  value: selectedPost.watchTime ?? '—',                            icon: 'ri-time-line',           color: 'text-orange-500' },
    { label: 'CTR',         value: selectedPost.ctr ?? '—',                                  icon: 'ri-cursor-line',         color: 'text-teal-500'   },
  ] : [];

  const cpsFactors = [
    { label: 'Engagement', score: cpsBreakdown.engagement, color: 'bg-violet-500' },
    { label: 'Sentiment',  score: cpsBreakdown.sentiment,  color: 'bg-violet-400' },
    { label: 'Reach',      score: cpsBreakdown.reach,      color: 'bg-violet-400' },
    { label: 'Historical', score: cpsBreakdown.historical, color: 'bg-violet-300' },
    { label: 'Format',     score: cpsBreakdown.format,     color: 'bg-violet-300' },
    { label: 'Platform',   score: cpsBreakdown.platform,   color: 'bg-violet-200' },
  ];

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        {/* Post metrics header skeleton */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div className="h-4 bg-gray-200 rounded w-44" />
            <div className="h-8 bg-gray-100 rounded-lg w-48" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                <div className="w-8 h-8 bg-gray-200 rounded-lg mb-2" />
                <div className="h-2.5 bg-gray-200 rounded w-12 mb-1.5" />
                <div className="h-5 bg-gray-200 rounded w-14" />
              </div>
            ))}
          </div>
        </div>
        {/* CPS score skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <div className="h-4 bg-gray-200 rounded w-32 mb-4" />
            <div className="flex items-center justify-center mb-4">
              <div className="w-24 h-24 bg-gray-200 rounded-full" />
            </div>
            <div className="space-y-2.5">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="h-2.5 bg-gray-100 rounded w-20" />
                  <div className="flex-1 h-2 bg-gray-100 rounded-full">
                    <div className="h-2 bg-gray-200 rounded-full" style={{ width: `${30 + i * 14}%` }} />
                  </div>
                  <div className="h-3 bg-gray-200 rounded w-8" />
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <div className="h-4 bg-gray-200 rounded w-44 mb-4" />
            <div className="grid grid-cols-2 gap-3">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                  <div className="h-3 bg-gray-200 rounded w-20 mb-2" />
                  <div className="h-6 bg-gray-200 rounded w-12 mb-1" />
                  <div className="h-2 bg-gray-100 rounded-full">
                    <div className="h-2 bg-gray-200 rounded-full" style={{ width: `${25 + i * 18}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* Theme + platform tracking skeletons */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <div className="h-4 bg-gray-200 rounded w-40 mb-4" />
              <div className="space-y-3">
                {[...Array(4)].map((_, j) => (
                  <div key={j} className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gray-200 rounded-lg flex-shrink-0" />
                    <div className="flex-1">
                      <div className="h-3 bg-gray-200 rounded w-3/4 mb-1.5" />
                      <div className="h-2 bg-gray-100 rounded-full">
                        <div className="h-2 bg-gray-200 rounded-full" style={{ width: `${20 + j * 20}%` }} />
                      </div>
                    </div>
                    <div className="h-4 bg-gray-200 rounded w-10" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (import.meta.env.VITE_ENABLE_ANALYTICS !== 'true') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[500px] bg-white border border-slate-200 rounded-2xl p-8 text-center shadow-sm relative overflow-hidden">
      {/* Background patterns */}
      <div className="absolute inset-0 bg-gradient-to-tr from-slate-50 via-white to-blue-50/20 opacity-70 pointer-events-none" />
      <div className="absolute -top-24 -left-24 w-48 h-48 rounded-full bg-blue-50 blur-3xl opacity-50" />
      <div className="absolute -bottom-24 -right-24 w-48 h-48 rounded-full bg-teal-50 blur-3xl opacity-50" />

      {/* Icon wrapper */}
      <div className="relative mb-6 z-10">
        <div className="w-20 h-20 bg-slate-50 border border-slate-100 rounded-2xl flex items-center justify-center shadow-inner animate-pulse">
          <i className="ri-bar-chart-grouped-line text-4xl text-blue-600"></i>
        </div>
        <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-blue-600 rounded-xl flex items-center justify-center shadow-lg border-2 border-white">
          <i className="ri-time-line text-white text-sm"></i>
        </div>
      </div>

      {/* Text */}
      <h3 className="text-2xl font-bold text-gray-900 mb-2 relative z-10">Performance Intelligence Engine</h3>
      <p className="text-sm text-gray-500 max-w-md mb-8 leading-relaxed relative z-10">
        Dive deep into post performance metrics, Content Performance Scores (CPS), sentiment analysis, and platform intelligence tracking. This feature is under active development.
      </p>

      {/* Premium Badge */}
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50/70 border border-blue-100 text-xs font-semibold text-blue-700 relative z-10">
        <i className="ri-compass-3-line"></i>
        Platform Integration Underway
      </div>
    </div>
  );
  }

  // Nothing stored yet: the first result is being prepared — not "no platforms connected".
  if (preparing) {
    return <PreparingNotice state={preparingState} onRetry={() => setReloadKey(k => k + 1)} />;
  }

  // A failed load, no calendar this month, or no connected platforms: say so instead of showing zeros.
  if (loadError || noPlan || performancePosts.length === 0) {
    return (
      <div className={`flex items-start gap-3 p-5 rounded-xl border ${loadError ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
        <i className={`${loadError ? 'ri-error-warning-line text-red-500' : 'ri-information-line text-amber-600'} text-lg leading-none mt-0.5`} />
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold ${loadError ? 'text-red-800' : 'text-amber-800'}`}>
            {loadError
              ? "Couldn't load performance data."
              : noPlan
                ? 'No content plan for this month yet — create one in Content Planning Engine.'
                : 'Connect Facebook/Instagram/LinkedIn/YouTube in Profile to see performance.'}
          </p>
          {loadError && <p className="text-xs text-red-700 mt-0.5">{loadError}</p>}
        </div>
        {loadError && (
          <button
            onClick={() => setReloadKey(k => k + 1)}
            className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-white border border-red-200 text-red-700 hover:bg-red-100 transition-colors whitespace-nowrap"
          >
            Retry
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <DataFreshnessBanner updatedAt={freshness.updatedAt} state={freshnessState} onRefresh={() => refetchQuietly(true)} />

      {calendarMonth && calendarMonth !== month && (
        <div className="flex items-center gap-3 p-3 rounded-lg border bg-amber-50 border-amber-200">
          <i className="ri-information-line text-amber-600 text-base leading-none" />
          <p className="text-xs text-amber-800">
            Showing {monthLabel(calendarMonth)} — no content plan for {monthLabel(month)} yet.
          </p>
        </div>
      )}

      {/* Post Performance Metrics Grid */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900 text-sm">Post Performance Metrics</h3>
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-gray-500">Post:</span>
            <select
              className="text-xs bg-gray-50 border border-gray-200 rounded-lg px-2 py-1 outline-none"
              value={selectedPost?.id || ''}
              onChange={e => {
                const post = performancePosts.find(p => p.id === e.target.value);
                if (post) setSelectedPost(post);
              }}
            >
              {performancePosts.map(p => (
                <option key={p.id} value={p.id}>
                  {p.title}{notLive(p) ? ' (not published yet)' : ''}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex items-center gap-2 mb-4">
          <i
            className={`${platformIcons[selectedPost?.platform] || 'ri-global-line'} text-sm w-4 h-4 flex items-center justify-center text-gray-600`}
          />
          <span className="text-xs font-medium text-gray-500">{selectedPost?.platform || '—'}</span>
          {notLive(selectedPost) ? (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
              Not published yet — no matching live post found
            </span>
          ) : (
            <span className="text-[10px] text-gray-400">· Engagement {fmtPct(selectedPost?.engagement)}</span>
          )}
        </div>
        <div className="grid grid-cols-4 gap-3">
          {metrics.map(m => (
            <div key={m.label} className="p-3 bg-gray-50/60 rounded-xl border border-gray-100">
              <div className="flex items-center gap-1.5 mb-2">
                <i className={`${m.icon} ${m.color} text-xs w-3 h-3 flex items-center justify-center`} />
                <span className="text-[11px] text-gray-500">{m.label}</span>
              </div>
              <p className="text-lg font-bold text-gray-900">{m.value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* CPS Score Card */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <div className="flex items-start gap-6">
          <div className="flex-shrink-0">
            <div
              className="w-24 h-24 rounded-full relative"
              style={{
                background: `conic-gradient(#8b5cf6 0% ${cpsBreakdown.score ?? 0}%, #e5e7eb ${cpsBreakdown.score ?? 0}% 100%)`,
              }}
            >
              <div className="absolute inset-3 bg-white rounded-full flex flex-col items-center justify-center">
                <span className="text-2xl font-bold text-gray-900">{cpsBreakdown.score ?? '—'}</span>
                <span className="text-[10px] text-gray-500">/ 100</span>
              </div>
            </div>
          </div>
          <div className="flex-1">
            <div className="mb-3">
              <h3 className="font-semibold text-gray-900 text-sm">Content Performance Score (CPS)</h3>
              <p className="text-[11px] text-gray-500">
                {cpsBreakdown.score == null
                  ? 'Shows once a post from this calendar is live.'
                  : 'Weighted rating across the dimensions your platforms report (— = not reported).'}
              </p>
            </div>
            <div className="grid grid-cols-3 gap-x-4 gap-y-2">
              {cpsFactors.map(d => (
                <div key={d.label}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[11px] text-gray-600">{d.label}</span>
                    <span className={`text-[11px] font-semibold ${typeof d.score === 'number' ? 'text-gray-900' : 'text-gray-400'}`}>{d.score ?? '—'}</span>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${d.color}`} style={{ width: `${((d.score ?? 0) / 25) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Audience Sentiment Intelligence */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <h3 className="font-semibold text-gray-900 text-sm mb-4">Audience Sentiment Intelligence</h3>
        {/* The backend sends null until Audience Intelligence has analysed real comments. */}
        {audienceSentimentBuckets.length === 0 && (
          <div className="py-6 text-center text-xs text-gray-400 bg-gray-50 rounded-xl border border-dashed border-gray-200">
            No sentiment data yet
          </div>
        )}
        <div className="grid grid-cols-3 gap-3">
          {audienceSentimentBuckets.map(bucket => {
            const meta = bucketMeta[bucket.color] || { bg: 'bg-gray-50', border: 'border-gray-200', iconText: 'text-gray-700' };
            return (
              <div key={bucket.label} className={`p-4 rounded-xl border ${meta.border} ${meta.bg}`}>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center bg-white">
                    <i className={`${bucket.icon} text-sm w-4 h-4 flex items-center justify-center ${meta.iconText}`} />
                  </div>
                  <span className={`text-xs font-semibold ${meta.iconText}`}>{bucket.label}</span>
                </div>
                <p className="text-2xl font-bold text-gray-900 mb-0.5">{fmtCount(bucket.count)}</p>
                <p className="text-[11px] text-gray-500">Responses clustered</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Theme Performance Analysis */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <h3 className="font-semibold text-gray-900 text-sm mb-4">Theme Performance Analysis</h3>
        <div className="space-y-3">
          {themeData.length === 0 && (
            <p className="text-xs text-gray-400">No themes in this month&apos;s content calendar yet.</p>
          )}
          {/* The content calendar's themes: planned / published posts, and once posts
              are live their engagement and trend — nothing for what hasn't happened yet. */}
          {themeData.map(t => (
            <div key={t.theme} className="flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-gray-700 truncate">{t.theme}</span>
                  <span className="text-xs text-gray-500">
                    {t.posts} {t.posts === 1 ? 'post' : 'posts'} · {t.published ?? 0} published
                  </span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  {typeof t.score === 'number' && (
                    <div className="h-full rounded-full bg-violet-500" style={{ width: `${t.score}%` }} />
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {t.label ? (
                  <span className={`text-[11px] px-2 py-0.5 rounded-md border font-medium ${labelBadgeClass(t.label)}`}>
                    {t.label}
                  </span>
                ) : (
                  <span
                    className="text-[11px] px-2 py-0.5 rounded-md border font-medium bg-gray-50 text-gray-400 border-gray-200"
                    title={typeof t.engagement === 'number'
                      ? 'The trend shows once two posts of this theme are live.'
                      : 'Shows once a post of this theme is published.'}
                  >
                    {typeof t.engagement === 'number' ? 'Trend after 2 posts' : 'Not published yet'}
                  </span>
                )}
                {t.trend != null && (
                  <span className={`text-xs font-semibold w-12 text-right ${String(t.trend).startsWith('+') ? 'text-emerald-600' : String(t.trend).startsWith('-') ? 'text-rose-600' : 'text-gray-500'}`}>
                    {t.trend}
                  </span>
                )}
                {typeof t.engagement === 'number' && (
                  <span className="text-sm font-bold text-gray-900 text-right" title="Average engagement of its live posts">
                    {t.engagement}%
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Platform Intelligence Tracking */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <h3 className="font-semibold text-gray-900 text-sm mb-4">Platform Intelligence Tracking</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr>
                <th className="text-[11px] font-medium text-gray-400 pb-3 pr-4">Platform</th>
                <th className="text-[11px] font-medium text-gray-400 pb-3 pr-4 text-right">Engagement</th>
                <th className="text-[11px] font-medium text-gray-400 pb-3 pr-4 text-right">Follower Growth</th>
                <th className="text-[11px] font-medium text-gray-400 pb-3 pr-4 text-right">Consistency</th>
                <th className="text-[11px] font-medium text-gray-400 pb-3 pr-4 text-right">Audience Quality</th>
                <th className="text-[11px] font-medium text-gray-400 pb-3 text-right">Sentiment Health</th>
              </tr>
            </thead>
            <tbody>
              {platformTracking.map(p => (
                <tr key={p.platform} className="border-t border-gray-100">
                  <td className="py-3 pr-4">
                    <div className="flex items-center gap-2">
                      <i className={`${platformIcons[p.platform] || 'ri-global-line'} text-sm w-4 h-4 flex items-center justify-center text-gray-600`} />
                      <span className="text-xs font-medium text-gray-700">{p.platform}</span>
                    </div>
                  </td>
                  {/* "—" while none of the platform's posts is matched to a live post. */}
                  <td className={`py-3 pr-4 text-right text-xs font-semibold ${typeof p.engagement === 'number' ? 'text-gray-900' : 'text-gray-400'}`}>{fmtPct(p.engagement)}</td>
                  <td className="py-3 pr-4 text-right">
                    {/* "N/A" (not fetched) is neutral gray, not a red decline. */}
                    <span className={`text-xs font-semibold ${String(p.followerGrowth).startsWith('+') && parseFloat(p.followerGrowth) > 5 ? 'text-emerald-600' : String(p.followerGrowth).startsWith('+') ? 'text-gray-600' : String(p.followerGrowth).startsWith('-') ? 'text-rose-600' : 'text-gray-400'}`}>
                      {p.followerGrowth ?? '—'}
                    </span>
                  </td>
                  <td className="py-3 pr-4 text-right">
                    <span className="text-xs font-semibold text-gray-900" title="Planned posts published">{fmtPct(p.consistency)}</span>
                  </td>
                  <td className="py-3 pr-4 text-right">
                    <span className={`text-xs font-semibold ${typeof p.audienceQuality === 'number' ? 'text-gray-900' : 'text-gray-400'}`}>{p.audienceQuality}</span>
                  </td>
                  <td className="py-3 text-right">
                    {/* "N/A" when the platform isn't connected or has no sentiment yet. Health is
                        positive + half of neutral, so 50 = all neutral: amber, not red. */}
                    <span className={`text-xs font-semibold ${typeof p.sentimentHealth !== 'number' ? 'text-gray-400' : p.sentimentHealth >= 80 ? 'text-emerald-600' : p.sentimentHealth >= 50 ? 'text-amber-600' : 'text-rose-600'}`}>
                      {p.sentimentHealth}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
